import {
    addDays,
    addHours,
    addMinutes,
    addMonths,
    addSeconds,
    addWeeks,
    addYears,
    getISOWeek,
    getISOWeekYear,
    startOfDay,
    startOfHour,
    startOfMonth,
    startOfWeek,
    startOfYear,
} from 'date-fns';

import { wholeUnitsBetween } from './diff';
import type { DateInput, PeriodUnit, TimeUnit } from './formats';
import { toDate } from './parse';

const ADD: Record<TimeUnit, (date: Date, amount: number) => Date> = {
    day: addDays,
    month: addMonths,
    year: addYears,
    hour: addHours,
    minute: addMinutes,
    second: addSeconds,
};

// Weeks start on Sunday, as moment's English default did.
const START: Record<PeriodUnit, (date: Date) => Date> = {
    hour: startOfHour,
    day: startOfDay,
    week: (date) => startOfWeek(date),
    month: startOfMonth,
    year: startOfYear,
};

const NEXT: Record<PeriodUnit, (date: Date) => Date> = {
    hour: (date) => addHours(date, 1),
    day: (date) => addDays(date, 1),
    week: (date) => addWeeks(date, 1),
    month: (date) => addMonths(date, 1),
    year: (date) => addYears(date, 1),
};

/**
 * A new Date `amount` units after `date`. Calendar units keep the local wall clock across
 * a DST switch; a month past the 31st clamps to the month's last day.
 *
 * @example
 * addTime(new Date(2024, 0, 31), 1, 'month'); // 29 February 2024
 */
export function addTime(date: DateInput, amount: number, unit: TimeUnit): Date | null {
    const d = toDate(date);
    if (!d) return null;
    return ADD[unit](d, amount);
}

/** A new Date `amount` units before `date`. */
export function subtractTime(date: DateInput, amount: number, unit: TimeUnit): Date | null {
    return addTime(date, -amount, unit);
}

/** The local start of the period that holds `date`. */
export function startOf(date: DateInput, period: PeriodUnit): Date | null {
    const d = toDate(date);
    if (!d) return null;
    return START[period](d);
}

/**
 * The last millisecond of the period that holds `date`: one before the next period
 * starts. On a day a DST switch repeats an hour, that is the second 23:59:59.999.
 */
export function endOf(date: DateInput, period: PeriodUnit): Date | null {
    const start = startOf(date, period);
    if (!start) return null;
    return new Date(START[period](NEXT[period](start)).getTime() - 1);
}

/** Whether `date1` is a later instant than `date2`; false if either is missing. */
export function isAfter(date1: DateInput, date2: DateInput): boolean {
    const d1 = toDate(date1);
    const d2 = toDate(date2);
    if (!d1 || !d2) return false;
    return d1.getTime() > d2.getTime();
}

/** Whether `date1` is an earlier instant than `date2`; false if either is missing. */
export function isBefore(date1: DateInput, date2: DateInput): boolean {
    const d1 = toDate(date1);
    const d2 = toDate(date2);
    if (!d1 || !d2) return false;
    return d1.getTime() < d2.getTime();
}

/**
 * Whole units from `date2` to `date1`, truncated toward zero; 0 if either is missing.
 *
 * @example
 * diffDates(new Date(2024, 0, 22), new Date(2024, 0, 15), 'day'); // 7
 */
export function diffDates(date1: DateInput, date2: DateInput, unit: TimeUnit): number {
    const d1 = toDate(date1);
    const d2 = toDate(date2);
    if (!d1 || !d2) return 0;
    return wholeUnitsBetween(d1, d2, unit);
}

export function now(): Date {
    return new Date();
}

/** Local midnight today. */
export function today(): Date {
    return startOfDay(new Date());
}

/** Local midnight tomorrow. */
export function tomorrow(): Date {
    return startOfDay(addDays(new Date(), 1));
}

/** Local midnight yesterday. */
export function yesterday(): Date {
    return startOfDay(addDays(new Date(), -1));
}

/** The ISO week number, or 0 for a missing date. */
export function getIsoWeek(date: DateInput): number {
    const d = toDate(date);
    return d ? getISOWeek(d) : 0;
}

/** The ISO week-numbering year, or 0 for a missing date. */
export function getIsoWeekYear(date: DateInput): number {
    const d = toDate(date);
    return d ? getISOWeekYear(d) : 0;
}

/** Minutes in milliseconds, for cache and polling intervals. */
export function minutesToMs(minutes: number): number {
    return minutes * 60 * 1000;
}
